import type { AgentRunFailureClass, AgentRunStatus, AgentRunType, AiOpsWindow } from '@saas/api-client';
import { z } from 'zod';

export const RUN_TYPES: AgentRunType[] = [
	'report_draft',
	'report_refine',
	'offer_proposal',
	'meeting_summary',
	'meeting_summary_regenerate',
	'summary_selection_edit',
	'report_selection_edit',
	'placement',
	'invoice_parse',
	'icon_classifier',
	'transcription',
];

export const RUN_STATUSES: AgentRunStatus[] = ['queued', 'running', 'completed', 'failed', 'superseded'];

export const FAILURE_CLASSES: AgentRunFailureClass[] = [
	'provider_error',
	'timeout',
	'verify_failed',
	'budget_exhausted',
	'malformed_output',
	'input_unavailable',
	'unknown',
];

export const WINDOW_IDS: AiOpsWindow[] = ['24h', '7d', '30d', '90d'];

export const DRILL_IN_PANEL_CLASS = '!w-[60vw] !max-w-[calc(100vw-24px)] lg:min-w-[760px]';

export const DEFAULT_WINDOW: AiOpsWindow = '7d';

export function isWindowId(value: string): value is AiOpsWindow {
	return WINDOW_IDS.some((id) => id === value);
}

export const aiOpsWindowSearchSchema = z.object({
	window: z.enum(WINDOW_IDS).default(DEFAULT_WINDOW),
});

export type AiOpsWindowSearch = z.infer<typeof aiOpsWindowSearchSchema>;

export const RUNS_VIEWS = ['runs', 'at_risk'] as const;

export type AiOpsRunsView = (typeof RUNS_VIEWS)[number];

export function isRunsView(value: string): value is AiOpsRunsView {
	return RUNS_VIEWS.some((view) => view === value);
}

export const aiOpsRunsSearchSchema = aiOpsWindowSearchSchema.extend({
	view: z.enum(RUNS_VIEWS).optional(),
	companyId: z.string().optional(),
	type: z.enum(RUN_TYPES).optional(),
	status: z.enum(RUN_STATUSES).optional(),
	failureClass: z.enum(FAILURE_CLASSES).optional(),
	search: z.string().optional(),
});

export type AiOpsRunsSearch = z.infer<typeof aiOpsRunsSearchSchema>;

export type SplitId = 'type' | 'outcome';

export function isSplitId(value: string): value is SplitId {
	return value === 'type' || value === 'outcome';
}

// An unrecorded figure renders as a dash, never a zero: "$0.00" reads as a measured spend of nothing.
export const NOT_RECORDED = '—';

export const usd = (value: number | null): string => (value === null ? NOT_RECORDED : `$${value.toFixed(2)}`);

export const seconds = (ms: number | null): string => (ms === null ? NOT_RECORDED : `${Math.round(ms / 1000)}s`);

export const tokens = (value: number | null): string => {
	if (value === null) return NOT_RECORDED;
	if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`;
	if (value >= 1_000) return `${(value / 1_000).toFixed(1)}k`;
	return value.toLocaleString();
};
